import React, { useState } from 'react';
import { X, Wallet, Eye, EyeOff, Search, TrendingUp, ChevronDown } from 'lucide-react';
import { apiFetch } from '../utils/api.ts';
import Swal from 'sweetalert2'; 

interface Activo {
  nombre: string;
  descripcion: string;
  cantidad: number;
  tipo: string;
  precio_actual: number;
  valor_total_usd: number;
}

interface BilleteraProps {
  activos: Activo[];
  total: number;
  onActualizar: () => void;
}

const Billetera: React.FC<BilleteraProps> = ({ activos, total, onActualizar }) => {
  const [showLista, setShowLista] = useState(false);
  const [ocultarSaldos, setOcultarSaldos] = useState(false);

  const getLogoUrl = (nombre: string, tipo: string) => {
    if (tipo === 'crypto') {
      return `https://raw.githubusercontent.com/spothq/cryptocurrency-icons/master/128/color/${nombre.toLowerCase()}.png`;
    }
    return `https://financialmodelingprep.com/image-stock/${nombre.toUpperCase()}.png`;
  };

  // const manejarEliminar = async (nombre: string) => {
  //   if (!window.confirm(`¿Estás seguro de eliminar ${nombre}?`)) return;
  //   try {
  //     const idLocal = 1;
  //     const response = await apiFetch(`/activos/${nombre}?id_usuario=${idLocal}`, {
  //       method: 'DELETE',
  //     });
  //     if (response.ok) {
  //       onActualizar();
  //       alert(`¡${nombre} eliminado con éxito!`);
  //     } else {
  //       const errorData = await response.json();
  //       alert(`Error: ${errorData.error}`);
  //     }
  //   } catch (error) {
  //     console.error('Error al borrar:', error);
  //   }
  // };

const manejarEliminar = async (nombre: string) => {
  if (!nombre) return;

  // 1. Confirmación estética
  const result = await Swal.fire({
    title: '¿Eliminar activo?',
    html: `¿Estás seguro de que deseas eliminar <b>${nombre}</b> de tu cartera?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#1e3a8a', // El azul de MyFinTrax
    cancelButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  });

  if (!result.isConfirmed) return;

  try {
    const idLocal = 1; // Ajustar según tu lógica de Auth o usar usuarioId.value
    const response = await apiFetch(`/activos/${nombre}?id_usuario=${idLocal}`, {
      method: 'DELETE',
    });
    
    if (response.ok) {
      //Ponemos actulizar inmediactamnte que salga todo ok.
      onActualizar();
      // 2. Alerta de éxito que se cierra sola
      await Swal.fire({
        title: '¡Eliminado!',
        text: `${nombre} ha sido quitado con éxito.`,
        icon: 'success',
        timer: 1500,
        showConfirmButton: false,
        timerProgressBar: true //barrita de tiempo.
      });


    } else {
      const errorData = await response.json();
      Swal.fire('Error', errorData.error || 'No se pudo eliminar el activo', 'error');
    }
  } catch (error) {
    console.error('Error al borrar:', error);
    Swal.fire('Error', 'Hubo un fallo en la conexión con el servidor', 'error');
  }
};


  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

        .billetera-root {
          font-family: 'Inter', sans-serif;
         
          width: 100%;
        }

        /* ── Trigger button ── */
        .btn-trigger {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 24px;
          background: #fff;
          border: 1.5px solid #E8E8E8;
          border-radius: 16px;
          cursor: pointer;
          transition: border-color 0.2s, box-shadow 0.2s;
          user-select: none;
        }
        .btn-trigger:hover {
          border-color: #C0C0C0;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
        }
        .btn-trigger.open {
          border-color: #1A1A1A;
        }

        .btn-trigger-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .btn-trigger-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #F4F4F4;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1A1A1A;
          transition: background 0.2s;
        }
        .btn-trigger.open .btn-trigger-icon {
          background: #1A1A1A;
          color: #fff;
        }
        .btn-trigger-label {
          font-size: 14px;
          font-weight: 500;
          color: #1A1A1A;
          letter-spacing: -0.01em;
        }
        .btn-trigger-chevron {
          color: #ABABAB;
          transition: transform 0.3s ease;
        }
        .btn-trigger.open .btn-trigger-chevron {
          transform: rotate(180deg);
          color: #1A1A1A;
        }

        /* ── Panel ── */
        .panel {
          background: #fff;
          border: 1.5px solid #E8E8E8;
          border-radius: 20px;
          overflow: hidden;
          margin-top: 8px;
          animation: panelIn 0.25s cubic-bezier(0.16,1,0.3,1);
        }
        @keyframes panelIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Balance header ── */
        .balance-header {
          padding: 32px 28px 24px;
          border-bottom: 1px solid #F0F0F0;
        }
        .balance-label {
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #ABABAB;
          margin-bottom: 10px;
        }
        .balance-row {
          display: flex;
          align-items: flex-end;
          gap: 10px;
          margin-bottom: 16px;
        }
        .balance-amount {
          font-family: 'DM Mono', monospace;
          font-size: 36px;
          font-weight: 500;
          color: #1A1A1A;
          letter-spacing: -0.03em;
          line-height: 1;
        }
        .balance-blur {
          font-family: 'DM Mono', monospace;
          font-size: 36px;
          font-weight: 500;
          color: #E8E8E8;
          letter-spacing: 0.1em;
          line-height: 1;
          user-select: none;
        }
        .balance-currency {
          font-size: 13px;
          font-weight: 500;
          color: #ABABAB;
          margin-bottom: 4px;
        }
        .eye-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          color: #CACACA;
          border-radius: 8px;
          display: flex;
          transition: color 0.15s, background 0.15s;
          margin-bottom: 2px;
        }
        .eye-btn:hover {
          color: #1A1A1A;
          background: #F4F4F4;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          background: #F0FAF4;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: #2D9D5C;
        }
        .status-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: #2D9D5C;
          animation: pulse 2s ease infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }

        /* ── Asset list ── */
        .asset-list-header {
          display: flex;
          justify-content: space-between;
          padding: 20px 28px 12px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #C0C0C0;
        }
        .asset-list-body {
          max-height: 440px;
          overflow-y: auto;
          padding: 0 12px 16px;
          scrollbar-width: thin;
          scrollbar-color: #E8E8E8 transparent;
        }
        .asset-list-body::-webkit-scrollbar { width: 4px; }
        .asset-list-body::-webkit-scrollbar-thumb {
          background: #E8E8E8;
          border-radius: 4px;
        }

        /* ── Asset card ── */
        .asset-card {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-radius: 12px;
          cursor: default;
          transition: background 0.15s;
          position: relative;
        }
        .asset-card:hover { 
          background: #c5c5c575;
        }
        .asset-card + .asset-card {
          border-top: 1px solid #ffffff;
        }

        .asset-left {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .asset-logo {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #F4F4F4;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid #EFEFEF;
        }
        .asset-logo img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .asset-logo-fallback {
          font-size: 11px;
          font-weight: 700;
          color: #ABABAB;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }
        .asset-name {
          font-size: 14px;
          font-weight: 600;
          color: #1A1A1A;
          letter-spacing: -0.01em;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .asset-badge {
          font-size: 9px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #4ca3eb;
          background: #F4F4F4;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .asset-qty {
          font-family: 'DM Mono', monospace;
          font-size: 12px;
          color: #000000;
          margin-top: 3px;
          font-weight: 400;
        }

        .asset-right {
          text-align: right;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .asset-right-values {
          text-align: right;
        }
        .asset-value {
          font-family: 'DM Mono', monospace;
          font-size: 14px;
          font-weight: 500;
          color: #1A1A1A;
          letter-spacing: -0.02em;
        }
        .asset-price {
          font-family: 'DM Mono', monospace;
          font-size: 11px;
          color: #2D9D5C;
          margin-top: 3px;
          font-weight: 400;
        }

        .delete-btn {
          opacity: 0;
          background: none;
          border: none;
          cursor: pointer;
          padding: 6px;
          border-radius: 8px;
          color: #000000;
          display: flex;
          transition: opacity 0.15s, color 0.15s, background 0.15s;
        }
        .asset-card:hover .delete-btn {
          opacity: 1;
        }
        .delete-btn:hover {
          color: #E53935;
          background: #FFF5F5;
        }

        /* ── Empty state ── */
        .empty-state {
          text-align: center;
          padding: 48px 24px;
          color: #C0C0C0;
        }
        .empty-state p {
          margin-top: 12px;
          font-size: 13px;
          font-weight: 500;
        }
      `}</style>

      <div className="billetera-root">
        {/* Trigger */}
        {/* <button
          className={`btn-trigger ${showLista ? 'open' : ''}`}
          onClick={() => setShowLista(!showLista)}
        >
          <div className="btn-trigger-left">
            <div className="btn-trigger-icon">
              <Wallet size={16} strokeWidth={2} />
            </div>
            <span className="btn-trigger-label">Ver Mi Patrimonio</span>
          </div>
          <ChevronDown size={16} strokeWidth={2} className="btn-trigger-chevron" />
        </button> */}

        <button
        onClick={() => setShowLista(!showLista)}
        className="ww-full py-4 rounded-2xl shadow-sm font-bold text-lg flex items-center justify-center gap-2 transition-all duration-300 border bg-[#FCD535] text-slate-900 border-[#FCD535] hover:shadow-md hover:bg-gray-500 hover:text-gray-100"
      >
        <Wallet size={22} />
        {showLista ? 'Cerrar billetera' : 'Ver mi patrimonio'}
      </button>

        {/* Panel */}
        {showLista && (
          <div className="panel">
            {/* Balance header */}
            <div className="balance-header">
              <div className="balance-label">Saldo total estimado</div>
              <div className="balance-row">
                {ocultarSaldos ? (
                  <span className="balance-blur">*****</span>
                ) : (
                  <span className="balance-amount">
                    ${(Number(total) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </span>
                )}
                {!ocultarSaldos && <span className="balance-currency">USD</span>}
                    <button 
                      onClick={() => setOcultarSaldos(!ocultarSaldos)}
                      className=" mb-5    flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-500 hover:text-blue-900 hover:border-indigo-200 rounded-full transition-all shadow-sm active:scale-5"
                      title={ocultarSaldos ? "Mostrar saldo" : "Ocultar"}
                    >
                      {ocultarSaldos ? <EyeOff size={18} /> : <Eye size={18} />}
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {ocultarSaldos ? "Mostrar" : ""}
                      </span>
                    </button>
              </div>
              <div className="status-badge">
                <div className="status-dot" />
                Mercado activo
              </div>
            </div>

            {/* Asset list */}
            <div className="asset-list-header">
              <span>Activo</span>
              <span>Valor</span>
            </div>

            <div className="asset-list-body">
              {activos && activos.length > 0 ? (
                activos.map((item, index) => (
                  <AssetCard
                    key={index}
                    activo={item}
                    logo={getLogoUrl(item.nombre, item.tipo)}
                    ocultar={ocultarSaldos}
                    onDelete={() => manejarEliminar(item.nombre)}
                  />
                ))
              ) : (
                <div className="empty-state">
                  <Search size={28} strokeWidth={1.5} />
                  <p>Buscando activos en la red…</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const AssetCard: React.FC<{
  activo: Activo;
  logo: string;
  ocultar: boolean;
  onDelete: () => void;
}> = ({ activo, logo, ocultar, onDelete }) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div className="asset-card">
      <div className="asset-left">
        <div className="asset-logo">
          {!imgError ? (
            <img
              src={logo}
              alt={activo.nombre}
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="asset-logo-fallback">{activo.nombre.substring(0, 2)}</span>
          )}
        </div>
        <div>
          <div className="asset-name">
            {activo.nombre}
            <span className="asset-badge">
              {activo.tipo === 'crypto' ? 'crypto' : 'Cedears'}
            </span>
          </div>
          <div className="asset-qty">
            {ocultar
              ? '****'
              : `${activo.cantidad.toLocaleString('es-AR')} ${activo.nombre}`}
          </div>
        </div>
      </div>

      <div className="asset-right">
        <div className="asset-right-values">
          <div className="asset-value">
            {ocultar
              ? '****'
              : `$${(Number(activo.valor_total_usd) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`}
          </div>
          <div className="asset-price">
            ${(Number(activo.precio_actual) || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <button
          className="delete-btn"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Eliminar activo"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

export default Billetera;